import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "@/api";
import { useAuth } from "@/auth";
import { useCountry } from "@/country";
import { useI18n } from "@/i18n";
import { ChatCircleDots, X, PaperPlaneTilt, Sparkle, Trash, ArrowsClockwise } from "@phosphor-icons/react";

const SESSION_KEY = "rail_assistant_session";

/* ---------------------------------------------------------------------------
   AssistantWidget — floating chat bubble, opens a Claude Sonnet 4.5 conversation.
   - Persists session_id in localStorage so users keep their chat across pages.
   - Aware of the active page + country.
   - Parses ```action JSON blocks and renders a confirm button.
   --------------------------------------------------------------------------- */
export default function AssistantWidget() {
  const { user } = useAuth();
  const { code: country } = useCountry();
  const { t, lang } = useI18n();
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(SESSION_KEY) || null);
  const [messages, setMessages] = useState([]); // [{role, text}]
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  // Load history when widget opens (if a session exists)
  useEffect(() => {
    if (open && sessionId && messages.length === 0) {
      api.get(`/assistant/history/${sessionId}`).then((r) => {
        setMessages((r.data.rows || []).map((m) => ({ role: m.role, text: m.text })));
      }).catch(() => {});
    }
  }, [open, sessionId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const greeting = lang === "fr"
    ? "Bonjour 👋 Je suis votre assistant Rail CRM. Je peux vous expliquer les méthodes M1/M2/M3, le simulateur What-if, analyser votre pipeline, ou suggérer des actions. Que voulez-vous savoir ?"
    : "Hi 👋 I'm your Rail CRM assistant. I can explain M1/M2/M3, the What-if simulator, analyse your pipeline, or suggest next actions. What would you like to know?";

  const suggestions = lang === "fr"
    ? ["C'est quoi M3 ?", "Mes 5 opportunités les plus chaudes", "Comment marche le What-if ?", "Quels plans dois-je prioriser ?"]
    : ["What is M3?", "My 5 hottest opportunities", "How does What-if work?", "Which plans should I prioritise?"];

  const send = async (text) => {
    const body = (text || input).trim();
    if (!body || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: body }]);
    setSending(true);
    try {
      const r = await api.post("/assistant/chat", {
        session_id: sessionId,
        message: body,
        page: location.pathname,
        country,
      });
      if (r.data.session_id && r.data.session_id !== sessionId) {
        setSessionId(r.data.session_id);
        localStorage.setItem(SESSION_KEY, r.data.session_id);
      }
      setMessages((m) => [...m, { role: "assistant", text: r.data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: lang === "fr"
        ? "⚠️ Erreur — l'assistant n'a pas pu répondre. Réessayez dans un instant."
        : "⚠️ Error — the assistant could not reply. Please try again." }]);
    } finally {
      setSending(false);
    }
  };

  const resetChat = async () => {
    if (sessionId) {
      try { await api.delete(`/assistant/history/${sessionId}`); } catch {}
    }
    localStorage.removeItem(SESSION_KEY);
    setSessionId(null);
    setMessages([]);
  };

  if (!user) return null;

  return (
    <>
      {/* Floating bubble */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-20 right-4 md:bottom-5 md:right-5 z-[1200] flex items-center justify-center transition-all hover:scale-105 active:scale-95"
          style={{
            width: 56, height: 56,
            background: "var(--accent)", color: "white",
            borderRadius: "50%",
            boxShadow: "0 10px 30px rgba(237,32,39,0.35), 0 4px 10px rgba(0,0,0,0.12)",
          }}
          aria-label="Open AI assistant"
          data-testid="assistant-open"
        >
          <Sparkle size={24} weight="fill" />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          className="fixed z-[1200] flex flex-col bottom-0 right-0 md:bottom-5 md:right-5 w-full md:w-[400px] md:max-h-[640px] md:rounded-[18px]"
          style={{
            background: "var(--bg-elevated)",
            border: "1px solid var(--border)",
            boxShadow: "var(--shadow-lg)",
            height: "100vh",
          }}
          data-testid="assistant-panel"
        >
          {/* Header */}
          <div className="px-4 py-3 flex items-center justify-between md:rounded-t-[18px]"
               style={{ background: "var(--text-primary)", color: "white" }}>
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: "var(--accent)" }}>
                <Sparkle size={15} weight="fill" />
              </div>
              <div>
                <div className="text-[14px] font-semibold leading-tight">Rail CRM Assistant</div>
                <div className="text-[10.5px] opacity-70 font-mono">Claude Sonnet 4.5 · {country}</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={resetChat}
                className="p-1.5 rounded-full hover:bg-white/10 transition-colors"
                title={lang === "fr" ? "Nouveau chat" : "New chat"}
                data-testid="assistant-reset"
              >
                <ArrowsClockwise size={14} />
              </button>
              <button
                onClick={() => setOpen(false)}
                className="p-1.5 rounded-full hover:bg-white/10 transition-colors"
                data-testid="assistant-close"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" style={{ background: "var(--bg)" }}>
            {messages.length === 0 && (
              <>
                <Bubble role="assistant" text={greeting} lang={lang} />
                <div className="space-y-1.5 mt-2">
                  {suggestions.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => send(s)}
                      className="w-full text-left px-3 py-2 rounded-[10px] text-[12.5px] hover:bg-[var(--bg-subtle)] transition-colors"
                      style={{ border: "1px solid var(--border)", color: "var(--text-primary)", background: "var(--bg-elevated)" }}
                      data-testid={`assistant-suggestion-${i}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </>
            )}
            {messages.map((m, i) => <Bubble key={i} role={m.role} text={m.text} lang={lang} country={country} />)}
            {sending && (
              <div className="flex gap-1.5 items-center px-3 py-2">
                <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "var(--accent)" }} />
                <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "var(--accent)", animationDelay: "0.2s" }} />
                <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "var(--accent)", animationDelay: "0.4s" }} />
              </div>
            )}
          </div>

          {/* Composer */}
          <div className="px-3 py-3" style={{ borderTop: "1px solid var(--border)" }}>
            <div className="flex gap-2 items-end">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                rows={1}
                placeholder={lang === "fr" ? "Posez une question…" : "Ask a question…"}
                className="input-tech"
                style={{ resize: "none", maxHeight: 120 }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
                }}
                data-testid="assistant-input"
              />
              <button
                onClick={() => send()}
                disabled={sending || !input.trim()}
                className="btn-accent shrink-0 flex items-center justify-center"
                style={{ width: 40, height: 40, padding: 0 }}
                data-testid="assistant-send"
              >
                <PaperPlaneTilt size={15} weight="fill" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


/* Single message bubble — extracts ```action JSON``` blocks to render confirm buttons. */
function Bubble({ role, text, lang, country }) {
  const isUser = role === "user";
  const parsed = parseActionBlocks(text);
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`px-3 py-2 rounded-[12px] text-[13.5px] leading-relaxed whitespace-pre-wrap max-w-[85%]`}
        style={{
          background: isUser ? "var(--accent)" : "var(--bg-elevated)",
          color: isUser ? "white" : "var(--text-primary)",
          border: isUser ? "none" : "1px solid var(--border)",
        }}
      >
        {parsed.text}
        {parsed.actions.map((a, i) => <ActionButton key={i} action={a} lang={lang} country={country} />)}
      </div>
    </div>
  );
}


function parseActionBlocks(text) {
  // Match ```action ... ``` fenced blocks
  const re = /```action\s*([\s\S]*?)```/g;
  const actions = [];
  let cleanText = text;
  let m;
  while ((m = re.exec(text))) {
    try {
      const parsed = JSON.parse(m[1].trim());
      actions.push(parsed);
    } catch (e) { /* skip malformed */ }
  }
  cleanText = text.replace(re, "").trim();
  return { text: cleanText, actions };
}


function ActionButton({ action, lang, country }) {
  const [status, setStatus] = useState("idle"); // idle | confirming | running | done | error
  const [error, setError] = useState(null);
  const label = {
    create_visit: lang === "fr" ? "Créer la visite" : "Create visit",
    create_contact: lang === "fr" ? "Créer le contact" : "Create contact",
    navigate: lang === "fr" ? "Y aller" : "Open",
  }[action.type] || "Run";

  const run = async () => {
    setStatus("running");
    try {
      const r = await api.post("/assistant/action", { ...action, country });
      if (r.data.type === "navigate") {
        window.location.href = r.data.path;
        return;
      }
      setStatus("done");
    } catch (e) {
      setError(e.response?.data?.detail || String(e));
      setStatus("error");
    }
  };

  return (
    <div className="mt-2 p-2 rounded-[8px] text-[12px]" style={{ background: "rgba(0,0,0,0.04)", border: "1px solid var(--border)" }}>
      <div className="font-mono text-[10.5px] uppercase mb-1.5" style={{ color: "var(--text-tertiary)" }}>{action.type}</div>
      <div className="text-[11.5px] mb-2" style={{ color: "var(--text-secondary)" }}>
        {Object.entries(action.args || {}).slice(0, 4).map(([k, v]) => (
          <div key={k}><b>{k}:</b> {String(v).slice(0, 50)}</div>
        ))}
      </div>
      {status === "done" ? (
        <div className="text-[12px]" style={{ color: "var(--success)" }}>✓ {lang === "fr" ? "Fait" : "Done"}</div>
      ) : status === "error" ? (
        <div className="text-[11px]" style={{ color: "var(--accent)" }}>⚠ {error}</div>
      ) : (
        <button
          onClick={run}
          disabled={status === "running"}
          className="btn-accent w-full text-[12px] py-1.5"
        >
          {status === "running" ? "…" : label}
        </button>
      )}
    </div>
  );
}
